import { FulltextTabView } from "./fulltext-tab";
import { CICStatus } from "./../../interfaces/cic-status";
import { AppEvents } from "./../../../app-events";
import { ParagraphFilterModel } from "./../../models/paragraph-filter";
import { JudgmentTabState } from "./../../models/tabs/judgment-tab-state";
import { DocumentEvents } from "./../../helpers/document-events";
import { KeyParagraphSentenceModel } from "./../../models/key-paragraph-sentence";
import { AppSettings } from "./../../../app-settings";
import { JudgmentTabModel } from "./../../models/tabs/judgment";
import { transient } from "aurelia-dependency-injection";
import { DisplayKeyParagraphModel } from "../../models/display-key-paragraph";
import { FullTextMode } from "../../models/document-state";
import "jquery.scrollto";
import "../../../shared/plugins/highlight";
import "waypoints/lib/jquery.waypoints";
import "jquery-ui/themes/base/core.css";
import "jquery-ui/themes/base/sortable.css";
import "jquery-ui/ui/widgets/sortable";
import "jquery-ui/ui/effect";
import { TermSanitiser } from "./../../helpers/search-term-sanitiser";

const scrollPosition = null;

const dom = {
    FULL_TEXT_DOCUMENT: ".full-text-document",
    COLLAPSE_PARA_NUM: ".para-num.collapsable",
    OPINION_GROUP: ".opinion-group",
    CONTEXT_BUTTONS: ".context-link",
    CONTEXT_BUTTONS_BLANK: ".no-context-link",
    PARAGRAPH_GROUP: ".para-group",
    FULL_TEXT_CONTENT: ".full-text-content",
    SEARCH_TERM_PREV: ".search-term-up",
    SEARCH_TERM_NEXT: ".search-term-down",
    PARAGRAPH_STEPPERS: ".paragraph-step",
    PARAGRAPH_NUMBER_TEXT: ".txt-paragraph-number",
    HIGHLIGHTED_PARAGRAPHS: "[class*='paragraph-highlight-']",
    HIGHLIGHT_BUTTON: ".highlight-button",
    HIGHLIGHT_CONTROLS: ".highlight-controls",
    CLEAR_FILTERS: ".clear-filters",
    FOOTNOTE_LINK: ".footnote-ref",
    FULLTEXT_NAV_LOADING: ".fulltext-nav-loading",
    HIGHLIGHTED_TEXT: ".text-highlight",
    PARAGRAPH_FILTER_BANNER: ".paragraph-filter-banner",
    PARAGRAPH_FILTER_BANNER_TEXT: ".paragraph-filter-banner-text",
    JUDGE_HEADING: ".judge-heading",
    JUDGMENT_HEADER: ".judgment-header",
    JUDGMENT_HEADING: ".heading",
    QUOTED_PASSAGES_BANNER: ".quoted-passages-banner",
    QUOTED_PASSAGES_BANNER_CLOSER: ".hide-quoted-passages",
    TOGGLE_HEADER_CONTENT: ".toggle-header-content",
    HEADER_CONTENT_CONTROLS: ".header-content-controls"
};

@transient()
export class JudgmentTabView extends FulltextTabView {

    public model: JudgmentTabModel;
    private isHeaderHidden: boolean;
    private noiseWords = ["the", "and", "not", "near", "within", "before", "after", "or", "a", "i", "v"];

    public constructor(options?: any) {
        super(options);
        this.isHeaderHidden = false;
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = super.events();
        events["click " + dom.PARAGRAPH_GROUP] = (e) => this.expandParagraph(e);
        events["keyup " + dom.PARAGRAPH_NUMBER_TEXT] = (e) => this.paragraphNumberTextBoxKeyUp(e);
        events["click " + dom.PARAGRAPH_STEPPERS] = (e) => this.paragraphStep(e);
        events["click " + dom.CLEAR_FILTERS] = (e) => this.resetFilters();
        events["click " + dom.HIGHLIGHT_BUTTON] = (e) => this.highlightClick(e);
        events["click " + dom.SEARCH_TERM_PREV] = (e) => this.searchTermPrevClick(e);
        events["click " + dom.SEARCH_TERM_NEXT] = (e) => this.searchTermNextClick(e);
        events["click " + dom.TOGGLE_HEADER_CONTENT] = () => this.toggleHeaderContent();
        events[`click .judgment-scroll-select`] = (e) => this.scrollSelectClick(e);
        events["click .popover-marker"] = (e) => this.showPopover(e);
        events["click " + dom.QUOTED_PASSAGES_BANNER_CLOSER] = (e) => {
            this.model.State.ShowPowerPara = false;
            this.model.State.ShowAllQuotes = false;
        };
        return events;
    }

    public onShow() {
        super.onShow();
        this.setupHighlighter();
        this.setupJudgmentScrollButtons();
        this.setupFooterPopover();
        this.setupHeaderControls();
        this.updateSearchTermNavigationButtonStatus(this.model.State.Highlighting);
        if (this.model.State.ParagraphNumber) {
            this.paragraphNumberChanged(null, this.model.State.ParagraphNumber);
        }
    }

    public setupModelListeners(): void {
        super.setupModelListeners();
        this.listenTo(this.model.State, "change:ParagraphNumber", this.paragraphNumberChanged);
        this.listenTo(this.model.State, "change:Highlighting", this.highlightingChanged);
        this.listenTo(this.model.State, "change:ShowAllQuotes", this.allQuotesToggle);
        this.listenTo(this.model.State, "change:ShowPowerPara", this.powerParaToggle);
        this.listenTo(this.model.State.ParagraphFilters, "add", this.paragraphFilterAdded);
        this.listenTo(this.model.State.ParagraphFilters, "remove", this.paragraphFilterRemoved);
        this.listenTo(this.model.State.ParagraphFilters, "reset", this.paragraphFiltersReset);

        this.listenTo(this.model.Data, DocumentEvents.HighlightAllQuotedParagraphs, () => {
            if (this.model.State.FullTextMode === FullTextMode.Text) {
                this.model.State.ShowAllQuotes = !this.model.State.ShowAllQuotes;
            }
        });
        this.listenTo(this.model.Data, DocumentEvents.ShowPowerPara, this.showPowerPara);
        this.listenTo(this.model.Data, DocumentEvents.ToggleCIC, this.toggleCitationsInContext);
    }

    protected toggleHeaderContent(): void {
        const fullText = this.$(".full-text-document");
        const headerContent = fullText.find(".para-group[data-num*='P']");
        if (!this.isHeaderHidden) {
            headerContent.addClass("hidden");
            this.$(dom.TOGGLE_HEADER_CONTENT).html(`<i class="fa fa-file-text" aria-hidden="true"></i> &nbsp;Show Header`);
            this.isHeaderHidden = true;
            if (this.model.State.Highlighting) {
                if (headerContent.find(".text-highlight.current-text-highlight-term").length > 0) {
                    this.moveSearchTermFocus(1);
                }
            }
        } else {
            headerContent.removeClass("hidden");
            this.$(dom.TOGGLE_HEADER_CONTENT).html(`<i class="fa fa-file-text" aria-hidden="true"></i> &nbsp;Hide Header`);
            this.isHeaderHidden = false;
        }
        this.$(dom.TOGGLE_HEADER_CONTENT).blur();
    }

    protected hideAllPopovers() {
        $(".paragraph-controls").popover("destroy");
        $(".popover").popover("hide");
    }

    /** Override for extra functionality */
    protected fullscreenReaderButtonClick(e) {
        this.appEvents.trigger(AppEvents.AppInsightsDocumentReadMode, {RenderType: this.model.Data.RenderType});
        this.model.State.ShowAllQuotes = false;
        this.model.State.ShowPowerPara = false;
        this.model.State.Highlighting = false;
        if (this.model.State.FullTextMode === FullTextMode.Text) {
            // Calling the below method causes a long chain of events, which include removing the paragraph highlighting/filters and leading to
            // the fulltext mode being set to TEXT (as opposed to PDF).  We still want this if is already text mode to remove the highlighting/filters
            // but if its PDF it incorrectly will switch to textmode meaning you can't see pdf in fullscreen reader mode
            this.resetFilters();
        }

        _.delay(() => {
            const visible = this.$(".fulltext-section:visible");
            const clone = visible.clone();
            this.appEvents.trigger("FullScreen", clone);
        }, 400);
    }

    protected switchToPdfViewMode(): void {
        this.$(dom.PARAGRAPH_FILTER_BANNER).hide();
        this.model.State.ShowAllQuotes = false;
        this.model.State.ShowPowerPara = false;
        this.model.State.ScrollPosition = this.$(".full-text-content.scrollable").scrollTop();
        this.model.State.FullTextMode = FullTextMode.Pdf;
    }

    protected switchToTextViewMode(): void {
        if (this.model.State.FullTextMode !== FullTextMode.Text) {
            if (this.$(dom.PARAGRAPH_FILTER_BANNER_TEXT).text().trim().length) {
                this.$(dom.PARAGRAPH_FILTER_BANNER).show();
            }
            this.$(".full-text-content.scrollable").css("z-index", "10");
            this.model.State.FullTextMode = FullTextMode.Text;
            this.$(".full-text-content.scrollable").scrollTop(scrollPosition);
        }
    }

    protected loadDocumentPdf(pdfLink: string): Promise<any> {
        return new Promise(() => {
            let protocolAddition = "";

            if (AppSettings.DocumentApiUrlPrefix.substr(0, 2) !== "//") {
                protocolAddition = window.location.protocol + "//" + window.location.hostname + "/";
            }

            let pdfUrl = `${protocolAddition}${AppSettings.DocumentApiUrlPrefix}judgmentpdf?docKey=${this.model.Data.Id}&pdfLink=${pdfLink}`;
            pdfUrl = encodeURIComponent(pdfUrl);
            const readerUrl = `assets/pdf-reader/web/viewer.html?file=${pdfUrl}&v=${AppSettings.AppVersion}`;
            const existing = this.$(".full-text-pdf iframe").attr("src");
            if (existing !== readerUrl) {
                const frame = $(`<iframe src='${readerUrl}' class='pdf-frame' frameborder="0"></iframe>`);
                this.$(".full-text-pdf").empty().append(frame);
            }
        });
    }

    private disableNonactiveContextLinkButtons() {
        const active = this.$("[class^='btn context-link active-']");
        this.toggleDisabledStateOfControl(this.$(dom.CONTEXT_BUTTONS).not(active), true);
    }

    private toggleDisabledStateOfControl(e: JQuery, disabled: boolean): void {
        e.toggleClass("disabled", disabled).attr("aria-disabled", disabled.toString());
    }

    private paragraphFilterAdded(model: ParagraphFilterModel, collection: Backbone.Collection<ParagraphFilterModel>): void {
        // Don"t show the judgment header band when paragraph filtering
        this.toggleJudgmentHeaders(true);
        this.toggleOrphanTags(true);
        // If we"re playing with the paragraph filters, go to the judgment
        this.$(".full-text-button[data-section='fulltext-judgment']").trigger("click");
        this.toggleParagraphFilter(model, true);
        if (collection.length === 4) {
            this.disableNonactiveContextLinkButtons();
        }
        this.model.State.FullTextMode = FullTextMode.Text;
        this.updateParagraphFilterCount(collection);
    }

    private updateParagraphFilterCount(filterCollection: Backbone.Collection<ParagraphFilterModel>) {
        let info = "";
        let referenceCount = 0;

        filterCollection.forEach((filter) => {
            referenceCount += filter.count;
        });

        if (filterCollection.length === 0) {
            this.$(dom.PARAGRAPH_FILTER_BANNER).fadeOut();
            this.$(dom.PARAGRAPH_FILTER_BANNER_TEXT).empty();
            return;
        } else if (filterCollection.length === 1) {
            info = referenceCount + " reference" + (referenceCount > 1 ? "s" : "") + " to <em>" + filterCollection.at(0).get("name") + "</em>";
        } else {
            info = referenceCount + " reference" + (referenceCount > 1 ? "s" : "") + " to " + filterCollection.length + " authorities";
        }
        this.$(dom.PARAGRAPH_FILTER_BANNER_TEXT).html(info);
        this.$(dom.PARAGRAPH_FILTER_BANNER).fadeIn();
        this.hideAllQuotes();
    }

    private toggleParagraphFilter(model: ParagraphFilterModel, apply: boolean): void {
        const target = model.id;
        const color = model.color;
        this.$(".paragraph-control").remove();
        this.$(dom.CONTEXT_BUTTONS).filter('[data-id = "' + target + '"]')
            .attr("aria-pressed", apply.toString())
            .toggleClass("active-" + color, apply);
        // hide all judge headings apart from those with expanded paras below
        this.$(dom.JUDGE_HEADING).addClass("hidden");

        this.$(dom.PARAGRAPH_GROUP).each((index, paraGroup) => {
            this.highlightLinks(target, color, apply, paraGroup);
        });

        this.appEvents.trigger(AppEvents.CitationHighlightToggle, { sourceId: this.model.Data.Id, targetId: target, color, apply });

        this.addParagraphControl();

        this.setupPositionCounter();
        if (!apply) {
            this.toggleDisabledStateOfControl(this.$(dom.CONTEXT_BUTTONS), false);
            if (!this.model.State.ScrollLock) {
                const $target = this.$(dom.HIGHLIGHTED_PARAGRAPHS).first().parents(".para-group");
                this.fullTextScroll($target);
            }
        } else {
            if (!this.model.State.ScrollLock) {
                const $target = this.$(".paragraph-highlight-" + color).first().parents(".para-group");
                this.fullTextScroll($target);
            }
        }
    }

    private highlightLinks(target, color, apply, group) {
        const $group = $(group);
        const $links = $group.find("a[data-id = " + target + "]");
        let hasLinks = $links.length > 0;

        // Check footnote refs for reference to the citation
        const $footRef = $($.parseHTML($group.find(".footnote-ref").data("content")));
        const $footrefLinks = $footRef.find("a[data-id = " + target + "]");

        if ($footrefLinks.length > 0) {
            hasLinks = true;
            $group.find(".footnote-ref").toggleClass("paragraph-highlight-" + color, apply);
        }

        $links.toggleClass("paragraph-highlight-" + color, apply);
        if (hasLinks && apply) {
            $group.removeClass("hidden");
            $group.addClass("filter-result");
            $group.prevAll(dom.JUDGE_HEADING).first().removeClass("hidden"); // show the judge heading for this para
        } else if (!this.currentlyHighlightingParagraph($group)) { // we only want to hide this paragraph if we're not highlighting something else in it
            $group.addClass("hidden");
            $group.removeClass("filter-result");
        }
    }

    // Checks to see if this paragraph is currently highlighting something
    private currentlyHighlightingParagraph(paragraph): boolean {
        return paragraph.find(dom.HIGHLIGHTED_PARAGRAPHS).length > 0;
    }

    private addParagraphControl(): void {
        let isFirst = true;
        this.$(dom.PARAGRAPH_GROUP).not(".filter-result").addClass("hidden");
        this.$(dom.PARAGRAPH_GROUP + ".filter-result").each((index, elem) => {
            const prior = $(elem).prevUntil(".para-group.filter-result").first().find(".para-num").text();
            let nextPrior = $(elem).prevUntil(".para-group.filter-result").last().find(".para-num").text();

            let prevNum = $(elem).nextUntil(".para-group.filter-result").filter(".para-group").first().find(".para-num").text();
            const nextNum = $(elem).nextUntil(".para-group.filter-result").filter(".para-group").last().find(".para-num").text();

            if (isFirst) {
                const template = $("<div class='btn-toolbar paragraph-control center' style='margin-bottom: 15px'></div>");
                const group = $("<div class='btn-group'></div>");

                if (!prior.includes("P")) {
                    nextPrior = "Header";
                }
                const up = this.createFirstShowPreviousParagraphButton(prior);
                const down = this.createFirstShowNextParagraphButton(nextPrior);
                const all = this.createFirstShowAllParagraphsButton();

                group.append(up)
                    .append(all)
                    .append(down);
                template.append(group);

                if ($(elem).hasClass("header")) {
                    this.$(dom.PARAGRAPH_GROUP + `[data-num*="P"]`).removeClass("hidden");
                    prevNum = $(elem).nextUntil(".para-group.filter-result").not(".header").filter(".para-group").first().find(".para-num").text();
                    if (!prevNum) {
                        prevNum = "1";
                    }
                } else {
                    $(elem).before(template);
                }
                isFirst = false;
            }

            if (prevNum && prevNum.length > 0 && nextNum && nextNum.length > 0) {
                const template = $("<div class='btn-toolbar paragraph-control center' style='margin-bottom: 15px'></div>");
                const group = $("<div class='btn-group'></div>");

                const up = this.createShowPreviousParagraphButton(prevNum);
                const down = this.createShowNextParagraphButton(nextNum);
                const all = this.createShowAllParagraphsButton();

                group.append(up)
                    .append(all)
                    .append(down);
                template.append(group);

                if ($(elem).hasClass("header")) {
                    $(elem).nextUntil(".para-group.filter-result").not(".header").filter(".para-group").first().before(template);
                } else {
                    $(elem).after(template);
                }
            }
        });
    }

    private createFirstShowNextParagraphButton(nextPrior: string) {
        const button = $("<button class='btn' data-page ='down' aria-label ='Show paragraph below'><span class='para-num-bottom'>" +
            nextPrior + "</span> <i class='fa fa-arrow-down' aria-hidden='true'></i></button>");
        button.click((event: JQueryEventObject) => {
            const start = this.$(event.target).parents(".paragraph-control");
            let para;
            if (nextPrior === "Header") {
                para = start.prevUntil(`.para-group[data-num="1P"]`).addBack().prev(`.para-group[data-num="1P"]`).addBack().filter(".header");
                para.removeClass("hidden");
                para.addClass("filter-result");
            } else {
                para = start.prevUntil(".para-group.filter-result").filter(".para-group").last();
                para.removeClass("hidden");
                para.addClass("filter-result");
            }

            if (para.nextUntil(".para-group.filter-result").length <= 1) {
                start.remove();
            } else {
                const newParaNum = start.prevUntil(".para-group.filter-result").filter(".para-group").last().find(".para-num").text();
                start.find(".para-num-bottom").text(newParaNum);
                nextPrior = newParaNum;
            }
        });
        return button;
    }

    private createFirstShowPreviousParagraphButton(prior: string) {
        const button = $("<button class='btn' data-page='up' aria-label='Show paragraph above'><span class='para-num-top'>" +
            prior + "</span> <i class='fa fa-arrow-up' aria-hidden='true'></i></button>");
        button.click((event: JQueryEventObject) => {
            const start = this.$(event.target).parents(".paragraph-control");
            const para = start.prevUntil(".para-group.filter-result").filter(".para-group").first();
            para.removeClass("hidden");
            para.addClass("filter-result");
            if (para.next(dom.JUDGMENT_HEADING).hasClass("hidden")) {
                para.next(dom.JUDGMENT_HEADING).removeClass("hidden");
                para.next(dom.JUDGMENT_HEADING).addClass("filter-result");
            }
            this.fullTextScroll(para, 800, -90);
            if (para.prevUntil(".para-group.filter-result").length === 0) {
                start.remove();
            } else {
                start.detach().insertBefore(para);
                const newParaNum = start.prevUntil(".para-group.filter-result").filter(".para-group").first().find(".para-num").text();
                start.find(".para-num-top").text(newParaNum);
            }
        });
        return button;
    }

    private createFirstShowAllParagraphsButton(): JQuery {
        const button = $("<button class='btn' aria-label='Show paragraph above' title='Show all paragraphs'>Show all paragraphs</button>");
        button.click((event: JQueryEventObject) => {
            const start = this.$(event.target).parents(".paragraph-control");
            const paras = start.prevUntil(".para-group.filter-result");
            paras.removeClass("hidden");
            paras.addClass("filter-result");
            start.remove();
        });
        return button;
    }

    private createShowPreviousParagraphButton(prevNum) {
        const button = $("<button class='btn' data-page='up' aria-label='Show paragraph above'><span class='para-num-top'>"
            + prevNum + "</span> <i class='fa fa-arrow-up' aria-hidden='true'></i></button>");
        button.click((event: JQueryEventObject) => {
            const start = this.$(event.target).parents(".paragraph-control");
            const para = start.nextUntil(".para-group.filter-result").filter(".para-group").first();
            para.removeClass("hidden");
            para.addClass("filter-result");
            if (para.prev(dom.JUDGMENT_HEADING).hasClass("hidden")) {
                para.prev(dom.JUDGMENT_HEADING).removeClass("hidden");
                para.prev(dom.JUDGMENT_HEADING).addClass("filter-result");
            }
            this.setupPositionCounter();
            this.fullTextScroll(para, 800, -90);
            if (para.nextUntil(".para-group.filter-result").length === 0) {
                start.remove();
            } else {
                start.detach().insertAfter(para);
                const newParaNum = start.nextUntil(".para-group.filter-result").filter(".para-group").first().find(".para-num").text();
                start.find(".para-num-top").text(newParaNum);
            }
        });
        return button;
    }

    private createShowNextParagraphButton(nextNum): JQuery  {
        const button = $("<button class='btn' data-page ='down' aria-label='Show paragraph below'><span class='para-num-bottom'>"
            + nextNum + "</span> <i class='fa fa-arrow-down' aria-hidden='true'></i></button>");
        button.click((event: JQueryEventObject) => {
            const start = this.$(event.target).parents(".paragraph-control");
            let para;
            if (nextNum === "Header") {
                para = start.nextAll(".para-group.header");
                para.removeClass("hidden");
                para.addClass("filter-result");
            } else {
                para = start.nextUntil(".para-group.filter-result").filter(".para-group").last();
                para.removeClass("hidden");
                para.addClass("filter-result");
            }

            if (para.next(dom.JUDGMENT_HEADING).hasClass("hidden")) {
                para.next(dom.JUDGMENT_HEADING).removeClass("hidden");
                para.next(dom.JUDGMENT_HEADING).addClass("filter-result");
            }

            this.setupPositionCounter();
            if (para.prevUntil(".para-group.filter-result").length <= 1) {
                start.remove();
            } else {
                let newParaNum = start.nextUntil(".para-group.filter-result").filter(".para-group").last().find(".para-num").text();
                if (newParaNum.includes("P")) {
                    newParaNum = "Header";
                }
                start.find(".para-num-bottom").text(newParaNum);
                nextNum = newParaNum;
            }
        });
        return button;
    }

    private createShowAllParagraphsButton(): JQuery {
        const button = $("<button class='btn' aria-label='Show paragraph above' title='Show all paragraphs'>Show all paragraphs</button>");
        button.click((event: JQueryEventObject) => {
            const start = this.$(event.target).parents(".paragraph-control");
            const paras = start.nextUntil(".para-group.filter-result");
            paras.removeClass("hidden");
            paras.addClass("filter-result");
            start.remove();
        });
        return button;
    }

    private toggleOrphanTags(hide: boolean): void {
        this.$(".full-text-document>p").each((index, p) => {
            const $tag = $(p);
            $tag.toggle(!hide);
        });
        this.$(".full-text-document>h3").each((index, heading) => {
            const $tag = $(heading);
            $tag.toggle(!hide);
        });
    }

    private toggleJudgmentHeaders(apply: boolean): void {
        this.$(dom.JUDGMENT_HEADER).each((index, judgment) => {
            const $judgment = $(judgment);
            $judgment.toggleClass("hidden", apply);
        });
        this.$(dom.JUDGMENT_HEADING).each((index, heading) => {
            const $heading = $(heading);
            $heading.toggleClass("hidden", apply);
        });
    }

    private powerParaToggle() {
        if (!this.model.State.ShowPowerPara) {
            this.hideAllQuotes();
            this.$(dom.QUOTED_PASSAGES_BANNER).fadeOut();
        }
    }

    private allQuotesToggle() {
        // Only want to show all quotes if full text is in text mode
        if (this.model.State.FullTextMode === FullTextMode.Text) {
            this.model.Data.trigger(DocumentEvents.AllQuotedParagraphsToggle, {showing: this.model.State.ShowAllQuotes}); // let the button know we're showing/hiding quoted paras
            if (this.model.State.ShowAllQuotes) {
                this.showAllQuotes();
                this.$(".all-quoted-button").addClass("active").attr("aria-pressed", "true");
            } else {
                this.hideAllQuotes();
                this.$(".all-quoted-button").removeClass("active").attr("aria-pressed", "false");
                this.$(dom.QUOTED_PASSAGES_BANNER).fadeOut();
            }
        }
    }

    private showAllQuotes() {
        this.resetFilters();
        this.$(`.full-text-button[data-section="fulltext-judgment"]`).trigger("click");
        let isLoaded = false;
        const $loadingDiv = this.$(".quoted-paras-loading");
        const $errorDiv = this.$(".quoted-paras-error");
        const $keyParaBanner = this.$(dom.QUOTED_PASSAGES_BANNER);
        // can only do this when both the fulltext and the key paragraphs are loaded
        this.model.loadQuotedParagraphs()
        .then(() => {
            isLoaded = true;
            $loadingDiv.hide();
            $errorDiv.hide();
            const $fullTextContent = this.$(dom.FULL_TEXT_CONTENT);
            const scroll = $fullTextContent.scrollTop();
            $fullTextContent.scrollTop(0).addClass("show-quotes");
            this.hideAllQuotes();
            this.model.DisplayQuotedParagraphs.each((para) => {
                this.showQuotedSentences(para);
            });

            this.setParaTrack();
            $keyParaBanner.show();
            $fullTextContent.scrollTop(scroll);
        }).catch((ex) => {
            $loadingDiv.hide();
            $errorDiv.show();
        });

        // if not loaded in half a second, tell the user that we"re loading
        _.delay(() => {
            if (!isLoaded) {
                $errorDiv.fadeOut();

            }
        }, 5000);
    }

    private showQuotedSentences(quotedParagraph: DisplayKeyParagraphModel) {
        const paragraphNumber = quotedParagraph.ParagraphNumber;
        const $fullTextPara = this.$(`.para-group[data-num="${paragraphNumber}"]`);
        $fullTextPara.addClass("quoted-para");
        this.convertParaToPlainText($fullTextPara);
        this.highlightQuotedSentences($fullTextPara, quotedParagraph);
    }

    private highlightQuotedSentences($fullTextPara: JQuery, quotedParagraph: DisplayKeyParagraphModel): void {
        quotedParagraph.ContentPieces.forEach((contentPiece) => {
            contentPiece.Sentences.forEach((sentence) => {
                const unique = this.makeId();
                const className = "quoted-sentence " + sentence.get("ScoreClass") + " " + unique + " popover-marker";
                const sentenceContent = sentence.RawContent.replace("  ", " ");
                $fullTextPara.highlight(sentenceContent, { className });
                _.defer(() => {
                    this.showSentenceQuotes(unique, sentence, quotedParagraph);
                });

                const quote = $fullTextPara.find("." + unique);
                let scoreClass = "0";
                if (sentence.ScoreClass) {
                    scoreClass = sentence.ScoreClass.replace("quote-level-", "");
                }

                quote.attr("data-selector", "true"); // workaround so that the title gets set properly
                quote.attr("aria-describedby", "Quoted passage level " + scoreClass);
                quote.attr("title", "Quoted passage level " + scoreClass);
            });
        });
    }

    private makeId(): string {
        let text = "";
        const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
        for (let i = 0; i < 5; i++) {
            text += possible.charAt(Math.floor(Math.random() * possible.length));
        }
        return text;
    }

    private showSentenceQuotes(id: string, sentence: KeyParagraphSentenceModel, para: DisplayKeyParagraphModel) {

        const self = this;
        const htmlContent = $(`<div><div class="quote-list-container"></div></div>`);
        const linkList = $(`<ul class="quote-list"></ul>`);
        const quoteIds = sentence.QuoteIds;
        const citingCaseIds = [];
        let paraList;
        let listItem;
        let link;

        quoteIds.forEach((quoteId) => {
            const quote = para.Quotes.find((item) => item.Id === quoteId);
            const newCase = !_.contains(citingCaseIds, quote.CitingCaseId);
            if (newCase) {
                paraList = $("<span class='quoting-para-number'></span>");
                listItem = $(`<li data-id='${quote.CitingCaseId}'><br /><span class='quote-court'>${quote.CitingCaseCourt}</span></li>`);
                link = $(`<a href="${quote.get("Url")}">${quote.CitingCaseTitle}</a>`);
                paraList.append(`<a data-para-num="${quote.CitingParagraphNumber}" href="${quote.get("Url").replace("/overview/", "/fulltext-judgment/")}/${quote.CitingParagraphNumber}" aria-label="Go to paragraph ${quote.CitingParagraphNumber}" title="Go to paragraph ${quote.CitingParagraphNumber}"><span>[${quote.CitingParagraphNumber}]</span></a>`);
                listItem.prepend(link);
                paraList.insertAfter(link);
                linkList.append(listItem);
                citingCaseIds.push(quote.CitingCaseId);
            } else {
                const paraItem = `<a data-para-num="${quote.CitingParagraphNumber}" href="${quote.get("Url").replace("/overview/", "/fulltext-judgment/")}/${quote.CitingParagraphNumber}" aria-label="Go to paragraph ${quote.CitingParagraphNumber}" title="Go to paragraph ${quote.CitingParagraphNumber}"><span>[${quote.CitingParagraphNumber}]</span></a>`;
                paraList = linkList.find(`[data-id="${quote.CitingCaseId}"]`).find(".quoting-para-number");
                if (paraList.html().indexOf(quote.get("Url").replace("/overview/", "/fulltext-judgment/") + "/" + quote.CitingParagraphNumber) === -1) {
                    paraList.append(paraItem);
                }
            }
        });
        const citelineButton = "<button class='btn quoted-citeline-button'>All<span class='citeline-icon'></span><span class='citeline-loading'><i class='fa fa-spinner fa-spin'/></span></button><span class='clear-fix'></span>";
        htmlContent.find(".quote-list-container").append(linkList);
        setTimeout(() => {
            ($(`.${id}`) as any).popover({
                html: true,
                title: "<strong>Quoted in</strong>" + citelineButton,
                placement: "left",
                viewport: "#desktop-container",
                content: htmlContent.html(),
                trigger: "manual",
                container: "#desktop-container"
            }).on("shown.bs.popover", () => {
                 $(".quote-list a").click((e) => {
                            $("." + id).popover("hide");
                            e.preventDefault();
                            const url = $(e.currentTarget).attr("href");
                            this.appEvents.trigger(AppEvents.AppInsightsDocumentKeyParaGotoLink, { Url: url});
                            Backbone.history.navigate(url, { trigger: true });
                 });
                 const $button = $(".quoted-citeline-button");
                 // tslint:disable-next-line:only-arrow-functions
                 $button.unbind("click").click(function() {
                     const popover = $("." + id);
                 });
            });
        }, 500);
    }

    private convertParaToPlainText($fullTextPara: JQuery): void {
        // store original HTML for later recovery
        $fullTextPara.data("originalHTML", $fullTextPara.html());
        $fullTextPara.find(".footnote-ref").remove();
        $fullTextPara.find("p, blockquote").each((idx, ele) => {
            const $ele = $(ele);
            if ($ele.find("p, blockquote").length) { return; }
            const textValue = $ele.text().replace(/\s+/g, " ");
            $ele.replaceWith(textValue);
            $ele.get(0).normalize();
        });
        if ($fullTextPara.get(0)) {
            $fullTextPara.get(0).normalize();
        }
    }

    private scrollSelectClick(e: JQueryEventObject): void {
        const $button = $(e.currentTarget);

        const $scrollNum = $button.attr("data-scroll-num");
        const $judgments = this.$(".judgment-header");

        const $judgmentToScroll = $judgments[$scrollNum];

        const container = this.$(dom.FULL_TEXT_CONTENT);

        container.scrollTo($judgmentToScroll, 800);
    }

    private setupHighlighter() {
        let foundTerms = false;
        const sanitisedTermsList = TermSanitiser.getSanitisedTermsList(this.model.Data.HighlightableSearchTerms);
        if (sanitisedTermsList && sanitisedTermsList.length > 0) {
            foundTerms = this.$(dom.FULL_TEXT_DOCUMENT).hasMatches(sanitisedTermsList, { wordsOnly: true });
        }

        if (foundTerms) {
            this.model.State.Highlighting = true;
            this.model.State.HighlightedTerms = this.$(".text-highlight").get();
            this.$(dom.HIGHLIGHT_BUTTON).removeAttr("disabled");
            this.$(dom.HIGHLIGHT_BUTTON).attr("title", "Highlight search terms. Currently not highlighting terms: " + this.noiseWords.join(", "));
        } else {
            this.$(dom.HIGHLIGHT_BUTTON).attr("disabled", "true");
            this.$(dom.HIGHLIGHT_BUTTON).attr("title", "Term Highlighting not available. Please close document and open from search results.");
        }
    }

    private highlightClick(e: JQueryEventObject): void {
        const currentState = !this.model.State.Highlighting;
        this.model.State.Highlighting = currentState;
        const $button = $(e.currentTarget);

        currentState ? $button.addClass("active") : $button.removeClass("active");
        $button.blur();
        if ($button.hasClass("active")) {
            this.model.State.HighlightedTerms = this.$(".text-highlight").get();
        }
        this.updateSearchTermNavigationButtonStatus(currentState);

        if (this.model.State.HighlightedTerms.length > 0) {
            const sanitisedSearchTerms = TermSanitiser.getSanitisedTermsList(this.model.Data.HighlightableSearchTerms);
            this.appEvents.trigger(AppEvents.AppInsightsDocumentHighlightTerm, {Highlighting: currentState, SearchTerms: sanitisedSearchTerms.toString()});
        }
    }

    private updateSearchTermNavigationButtonStatus(active: boolean) {
        this.toggleDisabledStateOfControl(this.$(dom.SEARCH_TERM_PREV), !active);
        this.toggleDisabledStateOfControl(this.$(dom.SEARCH_TERM_NEXT), !active);
    }

    private highlightingChanged(): void {
        const doHighlighting = this.model.State.Highlighting;
        this.$(dom.HIGHLIGHT_BUTTON).toggleClass("active", doHighlighting);
        if (doHighlighting) {
            const sanitisedTermsList = TermSanitiser.getSanitisedTermsList(this.model.Data.HighlightableSearchTerms);
            if (sanitisedTermsList.length > 0) {
                this.$(dom.FULL_TEXT_DOCUMENT).highlight(sanitisedTermsList, { wordsOnly: true });
                this.$(dom.HIGHLIGHTED_TEXT).first().addClass("current-text-highlight-term");
            }
        } else {
            this.$(dom.FULL_TEXT_DOCUMENT).unhighlight();
            this.updateSearchTermNavigationButtonStatus(false);
        }
    }

    private paragraphNumberChanged(model, paraNum) {
        // this.switchToTextViewMode();
        let target;
        if ($.trim(paraNum) === "") { return; }
        const taggedParas = this.$(dom.PARAGRAPH_GROUP);
        if (taggedParas.length) {
            target = taggedParas.filter(`[data-num="${paraNum}"]`);
        } else {
            // Paras incorrectly tagged
            target = this.$(".full-text-document > p").filter((index, tag) => {
                return $.trim($(tag).text()).indexOf(paraNum) === 0;
            }).first();
        }
        if (target.length !== 0) {
            this.$(dom.PARAGRAPH_NUMBER_TEXT).removeClass("has-error");
            if (target.length > 1) {
                // this.setupParaPopover(target);
                this.$(".paragraph-controls").popover("show");
            }
            else {
                    this.fullTextScroll(target, 500);
                    this.$(".paragraph-controls").popover("hide");
            }
        } else {
            this.$(dom.PARAGRAPH_NUMBER_TEXT).addClass("has-error");
        }
    }

    private toggleCitationsInContext(cicOptions): void {
        this.switchToTextViewMode();
        const $button = $(cicOptions.EventData.currentTarget);
        const count = $button.children(".context-count").text();

        this.appEvents.trigger(
            AppEvents.AppInsightsDocumentCitationsInContextClick,
            {RelationType: "Cited", CICDocId: $button.attr("data-id"), Selected: $button.attr("aria-pressed"), ButtonTitle: $button.attr("title")}
        );

        this.hideAllPopovers();

        const id = $button.attr("data-id");
        this.$(".full-text-button[data-section='fulltext-judgment']").trigger("click");
        this.model.State.ShowAllQuotes = false;
        this.model.State.ShowPowerPara = false;
        let newClass = "";
        if (this.$(dom.PARAGRAPH_GROUP).length > 0) {
            const cited = new Backbone.Collection();
            cited.add(this.model.Data.Considered.models);
            cited.add(this.model.Data.LegislationConsidered.models);
            newClass = this.model.State.addOrRemoveParagraphFilter({id, count: parseInt(count)}, cited);
        } else {
            // for docs with no paragraph numbers and proper paragraph groups, just scroll + highlight the first link
            // data"s not in the best shape, so make the best of it
            this.toggleHighlightingWithNoParaGroups(id, $button);
        }

        const eventData: CICStatus = {
            Class: newClass,
            Id: id,
            ActiveCount: this.model.State.ParagraphFilters.length
        };
        this.model.Data.trigger(DocumentEvents.CICEngaged, eventData);
        this.model.State.ParagraphFilters.length > 0 ? this.$(dom.TOGGLE_HEADER_CONTENT).hide() : this.$(dom.TOGGLE_HEADER_CONTENT).show();
        return;
    }

    private toggleHighlightingWithNoParaGroups(id: string, $button: JQuery) {
        const $links = this.$("a[data-id='" + id + "']");
        const colour = JudgmentTabState.highlightColors[0];
        // if it"s not already active, scroll to the first link
        if (!$button.hasClass("active-" + colour)) {
            const $firstLink = $links.first();
            this.fullTextScroll($firstLink, 800, -90);
        }
        // this singular colour will do for now
        $links.toggleClass("paragraph-highlight-" + colour);

        const active = $button.attr("class").indexOf("active-") > 0;
        $button.toggleClass("active-" + colour)
            .attr("aria-pressed", active.toString());
    }

    private setupJudgmentScrollButtons() {
        const judgments = this.$(".judgment-header").length;
        const judgmentDates = this.$(".judgment-date");
        if (judgments > 1) {
            const $group = $("<li class='judgment-scroll-selectors'><div class='btn-group'></div><li>");
            const button = `<button class="btn dropdown-toggle" data-toggle='dropdown'><i class="fa"></i>Judgments <span class="caret"></span></button>`;
            $group.find(".btn-group").append(button);
            $group.find(".btn-group").append("<ul class='dropdown-menu'></ul>");
            for (let i = 0; i < judgments; i++) {
                const judgment = `<li class="judgment-scroll-select" data-scroll-num=${i}><i class="fa"></i>${judgmentDates[i].textContent}</li>`;
                $group.find(".dropdown-menu").append(judgment);
            }

            this.$(".fulltext-section.fulltext-judgment .nav").append($group);
        }
    }

    private setParaTrack() {
        this.$(".para-track").remove();
        const $paraGroups = $(dom.PARAGRAPH_GROUP).not(".hidden");
        const fullTextContainerHeight = $(".full-text:visible").outerHeight() - 110; // :visible ensures that it gets the one for this doc
        const totalDocumentHeight = this.$(".full-text-document").outerHeight();
        const ratio = fullTextContainerHeight / totalDocumentHeight;
        const track = $(`<div class="para-track"></div>`);

        track.appendTo(this.$(".fulltext-section.fulltext-judgment")).height(fullTextContainerHeight);
        $paraGroups.each((idx, para: any) => {
            const paraHeight = $(para).outerHeight(true);
            const quotedSentences = $(para).find(".quoted-sentence");
            if (quotedSentences.length > 0) {
                const paraNum = $(para).attr("data-num");
                const className = "quote-level-" + this.getMaxQuotedLevel(quotedSentences);
                const unit = $(`<div id="para-track-${paraNum}" class="para-track-unit ${className}"></div>`);
                const relativeDocumentTop = this.$(".full-text-document").position().top;
                const top = $(para).position().top - relativeDocumentTop;
                const propTop = ratio * top;
                const height = Math.max(paraHeight / fullTextContainerHeight, 2) + "px";
                unit.css({ top: propTop, height });
                unit.appendTo(track);
            }
        });
    }

    private hideQuotedSentences(quotedParagraph: DisplayKeyParagraphModel) {
        const paragraphNumber = quotedParagraph.ParagraphNumber;
        const $fullTextPara = this.$(`.para-group[data-num="${paragraphNumber}"]`);
        $fullTextPara.removeClass("quoted-para");
        this.$(`#para-track-${paragraphNumber}`).remove();
        const originalHtml = $fullTextPara.data("originalHTML");
        $fullTextPara.html(originalHtml);
        _.defer(() => {
            this.setupFooterPopover();
        });

    }
    private setupFooterPopover(): void {
        this.$(dom.FOOTNOTE_LINK).each((idx, ele) => {
            const footRef = this.$(ele).data("footref");
            const note = this.$(`.footnote[data-footindex=\"${footRef}\"]:first`).html();
            this.$(ele).attr("data-content", note);
        });

        this.$(dom.FOOTNOTE_LINK).popover({
            container: "#application-container",
            viewport: "#application-container",
            placement: (context, source) => {
                const position = $(source).position();
                if (position.left > 515) {
                    return "left";
                }
                if (position.left < 515) {
                    return "right";
                }
                if (position.top < 110) {
                    return "bottom";
                }
                return "top";
            },
            html: true,
            template : '<div class="popover footnote-popover"><div class="arrow"></div><div class="popover-inner"><h3 class="popover-title"></h3><div class="popover-content"><p></p></div></div></div>',
            trigger: "manual"
        }).click((e) => {
            $(".footnote-popover").hide();
            $(e.currentTarget).popover("show");
            return false;
        });
    }

    private getMaxQuotedLevel(sentences: any) {
        const scores = [];
        sentences.each((idx, sentence) => {
            const score = parseInt($(sentence).attr("class").replace("quoted-sentence quote-level-", ""), 10);
            scores.push(score);
        });
        const max = Math.max.apply(Math, scores);
        return max + 1;
    }

    private hideAllQuotes() {
        this.$(dom.FULL_TEXT_CONTENT).removeClass("show-quotes");
        this.$(".para-track").remove();
        const quotedParas = this.model.DisplayQuotedParagraphs;
        if (quotedParas) {
            quotedParas.each((para: DisplayKeyParagraphModel) => {
                this.hideQuotedSentences(para);
            });
        }
    }

    private paragraphFilterRemoved(model: ParagraphFilterModel, collection: Backbone.Collection<any>) {
        // If we"re playing with the paragraph filters, go to the judgment
        this.$(".full-text-button[data-section='fulltext-judgment']").trigger("click");

        this.toggleParagraphFilter(model, false);
        if (collection.length === 0) {
            this.clearAllFilters();
        }
        this.model.State.FullTextMode = FullTextMode.Text;
        this.updateParagraphFilterCount(collection);
    }

    private resetFilters(): void {
        const paragraphFilters = this.model.State.ParagraphFilters;
        paragraphFilters.forEach((m: ParagraphFilterModel) => {
            this.appEvents.trigger(AppEvents.ClearCIC, {docKey: m.id}); // let other docs know we aren't showing this CIC anymore
        });
        paragraphFilters.reset();
        this.$(dom.FULL_TEXT_CONTENT).css({ top: "77px" });
        this.$(dom.TOGGLE_HEADER_CONTENT).show();
    }

    private paragraphFiltersReset(collection: Backbone.Collection<any>) {
        this.clearAllFilters();
        this.updateParagraphFilterCount(collection);
        this.model.Data.trigger(DocumentEvents.CICRemoveAll);
    }

    private clearAllFilters(): void {
        if (this.isHeaderHidden) {
            this.$(dom.PARAGRAPH_GROUP).not(".header").removeClass("hidden");
        } else {
            this.$(dom.PARAGRAPH_GROUP).removeClass("hidden");
        }
        this.$(dom.COLLAPSE_PARA_NUM).removeClass("collapsable");
        this.$(".para-group-all").remove();

        this.$(dom.HIGHLIGHTED_PARAGRAPHS).removeClass((index, css) => {
            const reg = css.match(/paragraph-highlight\-\S+/g);
            return (reg || []).join(" ");
        });
        this.$(dom.OPINION_GROUP).each((index, opinion) => {
            $(opinion).find("h2").first().removeClass("hidden");
            $(opinion).find("p").first().removeClass("hidden");
        });
        // for judge headings not in opinion groups, show them too
        this.$(dom.JUDGE_HEADING).removeClass("hidden");
        this.$(dom.CONTEXT_BUTTONS)
            .attr("aria-pressed", false.toString())
            .removeClass("disabled")
            .removeClass((index, css) => {
                const reg = css.match(/\active-\S+/g);
                return (reg || []).join(" ");
            });

        this.$(".paragraph-control").remove();

        this.appEvents.trigger(AppEvents.CitationHighlightToggle, { sourceId: this.model.Data.Id, targetId: "", color: "", apply: false });

        this.setupPositionCounter();

        this.fullTextScroll(0, 800, -45);
        // Show the judgment header band when not paragraph filtering
        this.toggleJudgmentHeaders(false);
        this.toggleOrphanTags(false);
    }

    private paragraphNumberTextBoxKeyUp(e: JQueryEventObject): void {
        const textBox = $(e.currentTarget);
        textBox.removeClass("has-error");
        if (textBox.hasClass("disabled")) { return; }
        if (this.paragraphTimer) { clearTimeout(this.paragraphTimer); }
        this.paragraphTimer = setTimeout(() => {
            const paraNum = textBox.val();
            this.model.State.ParagraphNumber = paraNum;
        }, 100);
    }

    private searchTermNextClick(e: JQueryEventObject): void {
        if (!this.$(dom.SEARCH_TERM_NEXT).hasClass("disabled")) {
            this.moveSearchTermFocus(1); // 1= next; -1; previous
        }
    }

    private searchTermPrevClick(e: JQueryEventObject): void {
        if (!this.$(dom.SEARCH_TERM_PREV).hasClass("disabled")) {
            this.moveSearchTermFocus(-1); // 1= next; -1; previous
        }
    }

    private showPowerPara(paragraphNumber: string): void {
        // Only want to show individual power paragraph if full text is in text mode.
        if (this.model.State.FullTextMode === FullTextMode.Text) {
            let isLoaded = false;
            const $loadingDiv = this.$(".quoted-paras-loading");
            const $errorDiv = this.$(".quoted-paras-error");

            this.model.loadQuotedParagraphs().then(() => {
                isLoaded = true;
                this.$(dom.QUOTED_PASSAGES_BANNER).show();
                $loadingDiv.hide();
                $errorDiv.hide();

                if (this.$(".paragraph-filter-banner:visible")) {
                    this.$(dom.CLEAR_FILTERS + ":visible").trigger("click");
                }

                if (!this.model.State.ShowAllQuotes) {
                    // Just this para, so toggle on/off
                    const clickedParaNum: number = parseInt(paragraphNumber, 10);
                    const para = this.model.DisplayQuotedParagraphs.where({ ParagraphNumber: clickedParaNum })[0];
                    const $fullTextPara = this.$(`.para-group[data-num="${clickedParaNum}"]`);
                    if (!$fullTextPara.hasClass("quoted-para")) {
                        this.showQuotedSentences(para as DisplayKeyParagraphModel);
                        this.setParaTrack();
                    }

                    const quotedParas = this.$(`.quoted-para`).length;
                    if (quotedParas === 0) {
                        this.model.State.ShowPowerPara = false;
                        this.$(dom.QUOTED_PASSAGES_BANNER).hide();
                    } else {
                        this.model.State.ShowPowerPara = true;
                    }
                }
                _.defer(() => {
                    const target = this.$("div[data-num=" + paragraphNumber + "]:visible").find(".quoted-sentence").first();
                    if (target.length !== 0) {
                        this.fullTextScroll(target, 500, -45);
                    }
                });
            }).catch(() => {
                $loadingDiv.hide();
                $errorDiv.show();
            });

            if (!this.hasGeneratedParaNumbers()) {
                this.$(dom.PARAGRAPH_NUMBER_TEXT + "[name='txt-paragraph-number']").val(paragraphNumber);
            }
            // if not loaded in half a second, show the user a loading message
            _.delay(() => {
                if (!isLoaded) {
                    $loadingDiv.show();
                    $errorDiv.hide();
                }
            }, 500);
        }
    }

    private moveSearchTermFocus(direction: number): void {
        const highlightedTerms = this.model.State.HighlightedTerms;
        const maxTermPos = highlightedTerms.length - 1;
        const currentTermPosn = this.$(highlightedTerms).index(this.$(".current-text-highlight-term"));
        if (currentTermPosn === 0 && direction === -1) {
            return;
        }

        if (currentTermPosn === -1) {
            this.$(this.$(highlightedTerms)).first().addClass("current-text-highlight-term");
        } else {
            let newPosn: number = currentTermPosn;
            if (this.model.State.ParagraphFilters.length > 0 || this.isHeaderHidden) {
                if (direction === 1) {
                    for (let i = currentTermPosn + 1; i < highlightedTerms.length; i++) {
                        const elem = this.$(highlightedTerms).get(i);
                        if (!this.$(elem).parents(".para-group").hasClass("hidden")) {
                            newPosn = i;
                            break;
                        }
                    }
                } else {
                    for (let i = currentTermPosn - 1; i >= 0; i--) {
                        const elem = this.$(highlightedTerms).get(i);
                        if (!this.$(elem).parents(".para-group").hasClass("hidden")) {
                            newPosn = i;
                            break;
                        }
                    }
                }
            } else {
                newPosn = (direction === 1) ? currentTermPosn + 1 : currentTermPosn - 1;
            }

            if (newPosn > maxTermPos) {
                newPosn -= 1;
            }

            this.$(this.$(highlightedTerms).get(currentTermPosn)).removeClass("current-text-highlight-term");
            this.$(this.$(highlightedTerms).get(newPosn)).addClass("current-text-highlight-term");
        }

        this.fullTextScroll(".current-text-highlight-term", 200, -40);

        // update para number on the nav bar
        const num = this.$(".current-text-highlight-term").parents(":eq(1)").attr("data-num");
        this.setCurrentParaNum(num);
    }

    /** Reacts to a click on a non-highlighted paragraph number when filtering by citations in context */
    private expandParagraph(e: JQueryEventObject): void {
        const clicked = $(e.currentTarget);
        if (clicked.hasClass("hidden")) {
            clicked.removeClass("hidden").find(".para-num").addClass("collapsable");
            this.setupPositionCounter();
            this.fullTextScroll(e.currentTarget, 800, -45);
            this.$(".filter-result").each((index, elem) => {
                $(elem).prevAll(".hidden:first").removeClass("hidden");
                $(elem).nextAll(".hidden:first").removeClass("hidden");
            });
        }
    }

    private paragraphStep(e: JQueryEventObject): void {
        const direction = $(e.currentTarget).attr("data-direction");
        let currentNum = this.getCurrentParaNum();
        if (!currentNum) {
            currentNum = this.$(dom.PARAGRAPH_GROUP).filter(".filter-result").first().attr("data-num");
        }
        const currentPara = this.$(dom.PARAGRAPH_GROUP).filter("[data-num='" + currentNum + "']");
        const allParas = this.$(dom.PARAGRAPH_GROUP);
        const currentIndex = $(allParas).index(currentPara);
        if (direction === "next") {
            const remaining = _.rest(allParas.toArray(), currentIndex + 1);
            const next = $(remaining).find("[class*='paragraph-highlight']").first().parent();
            // update para number on the nav bar
            if (next.length === 0) { return; }
            const nextNum = this.$(next).parents(".filter-result").attr("data-num");
            this.setCurrentParaNum(nextNum);
            this.fullTextScroll(next, 300, -45);
        } else if (direction === "prev") {
            const preceding = _.first(allParas.toArray(), currentIndex);
            const prev = $(preceding).find("[class*='paragraph-highlight']").last().parent();
            if (prev.length === 0) { return; }
            // update para number on the nav bar
            const prevNum = this.$(prev).parents(".filter-result").attr("data-num");
            this.setCurrentParaNum(prevNum);
            this.fullTextScroll(prev, 300, -45);
        }
    }

    private getCurrentParaNum() {
        let currentNum;
        if (this.hasGeneratedParaNumbers()) {
            currentNum = this.$(dom.PARAGRAPH_NUMBER_TEXT).attr("data-num");
        } else {
            currentNum = this.$(dom.PARAGRAPH_NUMBER_TEXT).val();
        }
        return currentNum;
    }

    private setCurrentParaNum(paraNum: any) {
        if (this.hasGeneratedParaNumbers()) {
            this.$(dom.PARAGRAPH_NUMBER_TEXT).attr("data-num", paraNum);
        } else {
            this.$(dom.PARAGRAPH_NUMBER_TEXT).val(paraNum);
        }
    }

    private fullTextScroll(selector, duration = 800, offsetTop = -45, easing = "easeInOutExpo") {
        this.clearPositionCounter();
        _.delay(() => {
            this.$(dom.FULL_TEXT_CONTENT).scrollTo(selector, duration, { easing, offset: { top: offsetTop, left: 0 }, axis: "y", onAfter: () => {
                this.setupPositionCounter();
            }});
        }, 550);
    }

    private clearPositionCounter() {
        if (this.waypoints) {
            _.each(this.waypoints, (wp: any) => { wp.destroy(); });
        }
    }

    private setupPositionCounter() {

        if (this.hasGeneratedParaNumbers()) {
            return;
        }

        if (this.$(dom.PARAGRAPH_GROUP + ":visible").length > 250) {
            // if there are too many paras, waypoints will crash the browser
            // a lack of live position counter isn't a disaster
            return;
        }

        const self = this;
        _.defer(() => {
            this.waypoints = this.$(dom.PARAGRAPH_GROUP + ":visible").waypoint({
                handler: function(direction) {
                    const $group = $(this.element);
                    const num = $group.attr("data-num");
                    if (!$group.hasClass("hidden") && !self.$(dom.PARAGRAPH_NUMBER_TEXT).is(":focus") && !self.hasGeneratedParaNumbers()) {
                        self.setCurrentParaNum(num);
                    }
                },
                context: this.$(dom.FULL_TEXT_CONTENT), offset: 45
            });
        });
    }

    private hasGeneratedParaNumbers(): boolean {
        return this.$(".generated").first().length > 0;
    }

    private showPopover(e: JQueryEventObject) {
        const $item = $(e.currentTarget);
        // Scroll the item to the middle of the screen
        const docHeight = this.$(".full-text-content").height();
        this.$(dom.FULL_TEXT_CONTENT).scrollTo($item, 800, {
            offset: { top: -(docHeight / 2), left: 0 },
            axis: "y",
            onAfter: () => {
                const slot = (this.model.Data as any).collection.parents[0].Slot;
                const compact = (this.model.Data as any).collection.parents[0].Compact;
                ($item as any).data("bs.popover").options.placement = ((slot === 1 && compact) ? "right" : "left");
                $item.popover("show");
            }
        });
    }

    private setupHeaderControls() {
        const fullTextType = this.model.Data.FullTextType;
        if (fullTextType === "HTML") {
            this.$(dom.HEADER_CONTENT_CONTROLS).hide();
            this.model.State.ShowHeaderControls = false;
        } else {
            this.$(dom.HEADER_CONTENT_CONTROLS).show();
            this.model.State.ShowHeaderControls = true;
        }
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/desktop/views/tabs/judgment.ts